import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from '../utils/SimpleRouter';
import { Home, Monitor, MessageSquare, FileText, Image, Settings, LogOut, Users, HelpCircle, UserCog, Presentation, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import { getUser, removeToken, removeUser, removeRefreshToken, isImpersonationWindow, getToken } from '../utils/storage';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [userName, setUserName] = useState('');
  const [inappropriateMessageCount, setInappropriateMessageCount] = useState(0);
  const [, forceUpdate] = useState(0);
  
  // Demo banner state
  const [demoInfo, setDemoInfo] = useState(null);

  useEffect(() => {
    // Check if this is an impersonation window
    if (isImpersonationWindow()) {
      setIsImpersonating(true);
    }
    
    // Get user name from localStorage
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        setUserName(user.name || user.email || '');
      }
    } catch (e) {
      console.error('Error getting user from localStorage:', e);
    }
    
    // Fetch organization name and inappropriate message count
    fetchOrganizationName();
    fetchInappropriateMessageCount();

    // Listen to language changes and force re-render
    const handleLanguageChanged = () => {
      forceUpdate(prev => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const fetchOrganizationName = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/organizations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizationName(response.data.name);
      
      // Check if this is a demo organization
      if (response.data.subscription_tier === 'demo' && response.data.demo_expires_at) {
        const expiresAt = new Date(response.data.demo_expires_at);
        const now = new Date();
        const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
        
        setDemoInfo({
          daysRemaining: Math.max(0, daysRemaining),
          expiresAt: expiresAt,
          isExpired: daysRemaining <= 0
        });
      } else {
        // Not a demo or no expiry date - hide banner
        setDemoInfo(null);
      }
      
      // Language priority: 
      // 1. User's explicit preferred_language (set during login from user record)
      // 2. Organization's default language
      // Only set organization language if user doesn't have a preferred language already set
      const userPreferredLang = localStorage.getItem('i18nextLng');
      const loginSetLanguage = sessionStorage.getItem('userLoginLanguageSet');
      
      // If login just set a user-specific language preference, don't override it
      if (loginSetLanguage) {
        console.log(`[CustomerLayout] User language was set during login, keeping: ${userPreferredLang}`);
        sessionStorage.removeItem('userLoginLanguageSet'); // Clear flag after first check
      } else if (!userPreferredLang && response.data.language) {
        // No user language set, use organization default
        await i18n.changeLanguage(response.data.language);
        console.log(`Language set to organization default: ${response.data.language}`);
      }
    } catch (error) {
      console.error('Failed to fetch organization name:', error);
    }
  };

  const fetchInappropriateMessageCount = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const inappropriateCount = response.data.filter(msg => msg.profanity_detected).length;
      setInappropriateMessageCount(inappropriateCount);
    } catch (error) {
      console.error('Failed to fetch inappropriate message count:', error);
    }
  };

  const handleLogout = () => {
    removeToken();
    removeRefreshToken();
    removeUser();
    // Clear session storage for language reset on next login
    sessionStorage.removeItem('userLanguageSet');
    toast.success(t('customerLayout.loggedOut'));
    navigate('/login');
  };

  const handleCloseImpersonation = () => {
    // Simply close the window when impersonating
    toast.success(t('customerLayout.closingWindow'));
    setTimeout(() => {
      window.close();
    }, 500);
  };

  // navItems - recalculated on every render when language changes
  const navItems = [
    { path: '/dashboard', icon: Home, label: t('customerLayout.overview') },
    { path: '/messages', icon: MessageSquare, label: t('customerLayout.messages') },
    { path: '/articles', icon: FileText, label: t('customerLayout.articles') },
    { path: '/images', icon: Image, label: t('customerLayout.banners') },
    { path: '/posters', icon: Presentation, label: t('customerLayout.posters') },
    { path: '/screens', icon: Monitor, label: t('customerLayout.screens') },
    { path: '/users', icon: Users, label: t('customerLayout.users') },
    { path: '/support', icon: HelpCircle, label: t('customerLayout.support') },
    { path: '/settings', icon: Settings, label: t('customerLayout.settings') }
  ];

  return (
    <div className="admin-layout">
      {/* Mobile Header with Hamburger */}
      <div className="mobile-header">
        <img 
          src="/infovegg-logo-inverted.svg" 
          alt="Infovegg.no" 
          style={{ height: '32px', width: 'auto' }}
        />
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className={`admin-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo" style={{ padding: '1rem 1rem 1.5rem', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '0.625rem' }}>
          <img 
            src="/infovegg-logo-inverted.svg" 
            alt="Infovegg.no" 
            style={{ height: '40px', width: 'auto' }}
          />
        </div>
        <nav className="admin-nav">
          {navItems.map(item => {
            const Icon = item.icon;
            const showBadge = item.label === 'Meldinger' && inappropriateMessageCount > 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ position: 'relative' }}
              >
                <Icon size={20} />
                {item.label}
                {showBadge && (
                  <span style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#ef4444',
                    color: '#ffffff',
                    borderRadius: '50%',
                    minWidth: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    padding: '0 0.3rem'
                  }}>
                    {inappropriateMessageCount}
                  </span>
                )}
              </Link>
            );
          })}
          
          {/* Show close button if impersonating */}
          {isImpersonating && (
            <button
              onClick={handleCloseImpersonation}
              style={{
                background: 'rgba(80, 227, 194, 0.2)',
                border: '1px solid #50E3C2',
                color: '#50E3C2',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                width: '100%',
                textAlign: 'left',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginBottom: '0.5rem'
              }}
            >
              <UserCog size={20} />
              {t('customerLayout.closeCustomerWindow')}
            </button>
          )}
          
          <div style={{ marginTop: 'auto', padding: '0 1rem 1rem' }}>
            {/* User name display */}
            {userName && (
              <div style={{
                padding: '0.75rem 0',
                marginBottom: '0.5rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#e0e0e0',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #50E3C2 0%, #3dd4b0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#1a1f2e'
                }}>
                  {userName.charAt(0).toUpperCase()}
                </span>
                <span style={{ fontWeight: '500' }}>{userName}</span>
              </div>
            )}
            <LanguageSwitcher />
          </div>
          <button
            onClick={handleLogout}
            style={{
              background: 'none',
              border: 'none',
              color: '#e0e0e0',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              width: '100%',
              textAlign: 'left',
              fontSize: '1rem'
            }}
          >
            <LogOut size={20} />
            {t('customerLayout.logout')}
          </button>
        </nav>

        {/* Byline footer - sticky to bottom */}
        <div style={{
          marginTop: 'auto',
          padding: '1rem 1rem 1.25rem',
          textAlign: 'left',
          fontSize: '0.75rem',
          color: '#FFFFFF',
          opacity: 0.5,
          letterSpacing: '0.15em',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          INFOVEGG.NO<br/>
          MADE BY DISPLAYSYSTEM.NO
        </div>
      </div>
      <div className="admin-main">
        {/* Demo Banner for demo customers */}
        {demoInfo && (
          <div style={{
            background: demoInfo.isExpired 
              ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)' 
              : demoInfo.daysRemaining <= 3 
                ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                : 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
            color: '#ffffff',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            fontSize: '0.85rem',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {demoInfo.isExpired ? (
              <>
                <span style={{ fontSize: '1rem' }}>⚠️</span>
                <span>Demoen har utløpt.</span>
                <a 
                  href="https://infovegg.no" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#ffffff', 
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}
                >
                  Bestill abonnement på infovegg.no
                </a>
              </>
            ) : (
              <>
                <span style={{ fontSize: '1rem' }}>⏰</span>
                <span>
                  {demoInfo.daysRemaining === 1 
                    ? '1 dag igjen av demo-perioden' 
                    : `${demoInfo.daysRemaining} dager igjen av demo-perioden`}
                </span>
                <span style={{ opacity: 0.8, marginLeft: '0.25rem', fontSize: '0.8rem' }}>
                  (utløper {demoInfo.expiresAt.toLocaleDateString('no-NO')})
                </span>
                <span style={{ margin: '0 0.25rem' }}>|</span>
                <a 
                  href="https://infovegg.no" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#ffffff', 
                    textDecoration: 'underline',
                    fontWeight: '500'
                  }}
                >
                  Bestill abonnement
                </a>
              </>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default CustomerLayout;
